import React, { useState } from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  ShieldCheck, 
  Sparkles, 
  Crown, 
  Layers, 
  BookOpen, 
  Users, 
  ArrowRight,
  AlertCircle
} from 'lucide-react';
import { UserProfile, AuthIntent } from '../types';

interface FAQPageProps {
  userProfile: UserProfile;
  setActiveTab: (tab: string) => void;
  openAuthModal: (mode?: 'signin' | 'signup', intent?: AuthIntent) => void;
  openPricingModal: () => void;
}

interface FAQItem {
  id: string;
  category: string;
  question: string;
  answer: React.ReactNode;
}

export const FAQPage: React.FC<FAQPageProps> = ({
  userProfile,
  setActiveTab,
  openAuthModal,
  openPricingModal
}) => {
  const [openIds, setOpenIds] = useState<Record<string, boolean>>({
    'affiliation': true,
    'pricing-model': true,
    'ai-estimated': true
  });

  const toggleFAQ = (id: string) => {
    setOpenIds(prev => ({ ...prev, [id]: !prev[id] }));
  };

  const faqItems: FAQItem[] = [
    {
      id: 'affiliation',
      category: 'Platform & Accreditation',
      question: 'Are you affiliated with or endorsed by CTC, ETS, TEA, or State Boards?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <p>
            <strong>No.</strong> PrePraxis Prep is an independent educator test preparation platform. We are not officially affiliated with, endorsed by, or operated by the California Commission on Teacher Credentialing (CTC), Educational Testing Service (ETS), Texas Education Agency (TEA), Pearson VUE, or any other state licensing authority.
          </p>
          <p>
            All test names, trademarks, and acronyms (including CBEST®, CSET®, NYSTCE®, TExES™, FTCE®, and PRAXIS®) belong to their respective trademark holders. Our curriculum, question banks, and simulated diagnostic papers are independently researched and carefully authored to reflect official public test specifications, domain percentage weightings, and state score benchmarks.
          </p>
        </div>
      )
    },
    {
      id: 'pricing-model',
      category: 'Subscription & Access',
      question: 'How does PrePraxis All-Access compare to other test prep services?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <p>
            Other test prep platforms frequently charge <strong>up to $50/month per individual subtest or exam</strong>. If you are taking multiple subtests (e.g. CSET Multiple Subjects Subtests I, II, and III, or Praxis Core + Subject Assessments), costs can quickly exceed $150/month.
          </p>
          <p>
            PrePraxis gives you <strong>unlimited access to every single exam family and state paper</strong> for one flat rate—starting as low as $28/month. There are zero per-exam upsells, hidden fees, or locked subtest paywalls.
          </p>
        </div>
      )
    },
    {
      id: 'cancellation',
      category: 'Subscription & Access',
      question: 'Can I cancel my subscription at any time?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <p>
            Yes. You can manage and cancel your subscription anytime directly through your Account settings in one click. Once cancelled, you retain complete all-access privileges until the end of your current paid billing period, with no further renewals or charges.
          </p>
        </div>
      )
    },
    {
      id: 'free-preview',
      category: 'Subscription & Access',
      question: 'What is included in the free practice preview?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <p>
            Every single exam paper on PrePraxis includes a <strong>5-question diagnostic preview</strong> with complete step-by-step rationales, domain tagging, and interactive study modes. You can explore the full catalog, review study outlines, and test question quality before entering payment details.
          </p>
        </div>
      )
    },
    {
      id: 'ai-estimated',
      category: 'AI Scoring & Diagnostics',
      question: 'What does the "AI-estimated" score badge mean?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-amber-50 border border-amber-300 text-amber-900 text-xs font-semibold mb-1">
            <AlertCircle className="w-3.5 h-3.5 text-amber-600" />
            <span>AI-estimated — not an official state score</span>
          </div>
          <p>
            For constructed responses, essay submissions, and full-length mock attempts, our platform uses machine learning psychometric models calibrated to official state scoring rubrics (e.g. 1–6 holistic scale for Praxis writing, 1–4 scale for CSET constructed response).
          </p>
          <p>
            These estimates provide instant formative feedback and diagnostic readiness indicators to guide your study. However, they are diagnostic estimates generated by PrePraxis algorithms and do not constitute an official passing score issued by testing authorities.
          </p>
        </div>
      )
    },
    {
      id: 'accuracy',
      category: 'Content Accuracy & Updates',
      question: 'How frequently are questions and blueprints updated?',
      answer: (
        <div className="space-y-2 text-xs sm:text-sm text-slate-600 leading-relaxed">
          <p>
            Our content team continuously tracks updates released by ETS, CTC, TEA, NYSED, FLDOE, ISBE, PDE, and GaPSC. When testing boards adjust domain frameworks, passing score conversions, or introduce new question formats (such as constructed response prompts or digital calculator guidelines), our test papers and simulated mock exams are promptly updated.
          </p>
        </div>
      )
    }
  ];

  const categories = Array.from(new Set(faqItems.map(item => item.category)));

  return (
    <div className="max-w-4xl mx-auto space-y-10 py-6 sm:py-10">
      {/* Header */}
      <div className="text-center space-y-3">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-indigo-50 border border-indigo-200 text-indigo-700 text-xs font-bold">
          <HelpCircle className="w-3.5 h-3.5 text-indigo-600" />
          <span>Frequently Asked Questions</span>
        </div>
        <h1 className="text-3xl sm:text-4xl font-black text-slate-900 tracking-tight">
          Everything You Need to Know About PrePraxis
        </h1>
        <p className="text-sm sm:text-base text-slate-600 font-medium max-w-2xl mx-auto leading-relaxed">
          Clear, transparent answers about our test specifications, subscription model, AI scoring diagnostics, and state alignment.
        </p>
      </div>

      {/* Quick Category Anchor Navigation */}
      <div className="flex flex-wrap justify-center gap-2">
        {categories.map(cat => (
          <span
            key={cat}
            className="px-3 py-1.5 bg-white border border-slate-200 rounded-lg text-xs font-bold text-slate-700 shadow-2xs"
          >
            {cat}
          </span>
        ))}
      </div>

      {/* Accordion Group */}
      <div className="space-y-4">
        {faqItems.map((item) => {
          const isOpen = !!openIds[item.id];
          return (
            <div
              key={item.id}
              className="bg-white rounded-2xl border border-slate-200 shadow-xs overflow-hidden transition"
            >
              <button
                onClick={() => toggleFAQ(item.id)}
                className="w-full p-5 sm:p-6 text-left flex items-start justify-between gap-4 hover:bg-slate-50/60 transition cursor-pointer"
              >
                <div className="space-y-1">
                  <span className="text-[11px] font-bold uppercase tracking-wider text-indigo-600">
                    {item.category}
                  </span>
                  <h3 className="text-base sm:text-lg font-bold text-slate-900 leading-snug">
                    {item.question}
                  </h3>
                </div>
                <div className="shrink-0 w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-600">
                  {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </div>
              </button>

              {isOpen && (
                <div className="px-5 sm:px-6 pb-6 pt-1 border-t border-slate-100 bg-slate-50/40">
                  {item.answer}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Bottom CTA Block — Ask Community */}
      <div className="bg-gradient-to-r from-indigo-900 to-slate-900 text-white rounded-3xl p-6 sm:p-8 border border-indigo-500/20 shadow-xl flex flex-col sm:flex-row items-center justify-between gap-6">
        <div className="space-y-2 text-center sm:text-left">
          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-indigo-500/20 border border-indigo-400/30 text-indigo-300 text-xs font-semibold">
            <Users className="w-3.5 h-3.5 text-indigo-400" />
            <span>Peer Support & Tutor Network</span>
          </div>
          <h2 className="text-xl sm:text-2xl font-bold tracking-tight">
            Still Have Questions?
          </h2>
          <p className="text-xs sm:text-sm text-slate-300 max-w-lg leading-relaxed">
            Connect with certified teachers, subject specialists, and fellow candidates preparing for exams across all 50 states.
          </p>
        </div>

        <div className="flex flex-col sm:flex-row items-center gap-3 shrink-0 w-full sm:w-auto">
          <button
            onClick={() => {
              if (!userProfile?.uid || userProfile.uid === 'guest_user_1') {
                openAuthModal('signin', { actionType: 'community', tab: 'forum', contextTitle: 'Community' });
              } else {
                setActiveTab('forum');
              }
            }}
            className="w-full sm:w-auto px-6 py-3 bg-indigo-600 hover:bg-indigo-500 text-white font-extrabold rounded-xl shadow-lg transition flex items-center justify-center gap-2 text-xs sm:text-sm cursor-pointer"
          >
            <Users className="w-4 h-4" />
            <span>Ask the Community</span>
            <ArrowRight className="w-4 h-4" />
          </button>
          <button
            onClick={openPricingModal}
            className="w-full sm:w-auto px-5 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 border border-slate-700 font-bold rounded-xl transition text-xs sm:text-sm cursor-pointer"
          >
            <span>View All-Access Plans</span>
          </button>
        </div>
      </div>
    </div>
  );
};
